import { useCallback, useEffect, useRef, useState } from "react";
import { liveMode, OFFLINE_POLL_MS, PING_MS, PONG_TIMEOUT_MS, reconnectDelay, SYNC_TIMEOUT_MS } from "../../domain/live";
import { api, ApiError, reportUnreachable, unwrap, type ChangesResponse, type EventChange, type EventData, type EventDetail, type EventHeader, type GlobalSettings, type PackRef } from "../../api/client";

export interface LiveState {
  event: EventData | null;
  packs: PackRef[];
  pack: PackRef | null;
  errors: string[];
  /** administrator defaults in force, shown next to the inherited settings (SW-PA-01) */
  global: GlobalSettings | null;
  /** WebSocket connected */
  live: boolean;
  /** instant of the last successful synchronisation with the server (SW-WS-02) */
  lastSyncAt: Date | null;
  /** the last synchronisation failed (server unreachable) */
  syncFailed: boolean;
  deleted: boolean;
  loadError: unknown;
}

const EMPTY: LiveState = { event: null, packs: [], pack: null, errors: [], global: null, live: false, lastSyncAt: null, syncFailed: false, deleted: false, loadError: null };

/**
 * Event of the workspace kept in sync with the server (architecture §8): snapshot at opening, WebSocket
 * notifications, then `/changes` since the known revision. Writes call [sync] right away so that the
 * console shows the server state, including what other operators changed in between (SW-WS-30).
 */
export function useLiveEvent(id: string | undefined, onChanges?: (changes: EventChange[]) => void) {
  const [state, setState] = useState<LiveState>(EMPTY);
  const eventRef = useRef<EventData | null>(null);
  const syncing = useRef<Promise<void> | null>(null);
  const ws = useRef<WebSocket | null>(null);
  const retry = useRef(0);
  const changesHandler = useRef(onChanges);
  changesHandler.current = onChanges;

  const setEvent = useCallback((e: EventData | null) => { eventRef.current = e; setState((s) => ({ ...s, event: e })); }, []);
  const synced = (s: LiveState): LiveState => ({ ...s, lastSyncAt: new Date(), syncFailed: false });
  const unreachable = (e: unknown) => !(e instanceof ApiError);

  const reload = useCallback(async () => {
    if (!id) return;
    try {
      const d: EventDetail = unwrap(await api.GET("/events/{id}", { params: { path: { id } }, signal: AbortSignal.timeout(SYNC_TIMEOUT_MS) }));
      eventRef.current = d.event;
      setState((s) => synced({ ...s, event: d.event, packs: d.packs, pack: d.pack ?? d.packs[0] ?? null, errors: d.errors, global: d.global ?? null, loadError: null, deleted: false }));
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setState((s) => ({ ...s, deleted: true, loadError: e }));
      else setState((s) => ({ ...s, loadError: e, syncFailed: unreachable(e) }));
    }
  }, [id]);

  /** Fetches and applies the changes since the known revision (serialised). */
  const sync = useCallback(async () => {
    if (!id) return;
    if (syncing.current) return syncing.current;
    const run = (async () => {
      const cur = eventRef.current;
      if (!cur) { await reload(); return; }
      try {
        const r: ChangesResponse = unwrap(await api.GET("/events/{id}/changes", { params: { path: { id }, query: { since: cur.revision ?? 0, hcSince: cur.heightCorrectionsRevision ?? 0 } }, signal: AbortSignal.timeout(SYNC_TIMEOUT_MS) }));
        if (r.changes.length === 0 && r.heightCorrections == null) {
          if ((cur.heightCorrectionsRevision ?? 0) !== r.heightCorrectionsRevision) setEvent({ ...cur, heightCorrectionsRevision: r.heightCorrectionsRevision });
          setState(synced);
          return;
        }
        setEvent(applyChanges(eventRef.current ?? cur, r));
        setState(synced);
        if (r.changes.length > 0) changesHandler.current?.(r.changes);
      } catch (e) {
        if (e instanceof ApiError && (e.code === "SNAPSHOT_REQUIRED" || e.status === 404)) await reload();
        else { setState((s) => ({ ...s, syncFailed: unreachable(e) })); throw e; }
      }
    })();
    syncing.current = run.finally(() => { syncing.current = null; });
    return syncing.current;
  }, [id, reload, setEvent]);

  useEffect(() => { eventRef.current = null; setState(EMPTY); void reload(); }, [id, reload]);

  // live notifications
  useEffect(() => {
    if (!id) return;
    let closed = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let pongTimer: ReturnType<typeof setTimeout> | null = null;
    const quiet = () => sync()?.catch(() => undefined);
    const scheduleReconnect = () => { if (timer) clearTimeout(timer); timer = setTimeout(connect, reconnectDelay(retry.current++)); };
    /** the channel is given up at once (no answer to the ping, browser offline): polling and banner take over */
    const drop = (sock: WebSocket | null) => {
      if (!sock || ws.current !== sock) return;
      sock.onopen = sock.onmessage = sock.onclose = sock.onerror = null;
      try { sock.close(); } catch { /* already closed */ }
      ws.current = null;
      if (pongTimer) { clearTimeout(pongTimer); pongTimer = null; }
      setState((s) => ({ ...s, live: false }));
      void quiet();
      if (!closed) scheduleReconnect();
    };
    function connect() {
      if (closed) return;
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      const sock = new WebSocket(`${proto}://${window.location.host}/api/v1/events/${encodeURIComponent(id!)}/live`);
      ws.current = sock;
      sock.onopen = () => { retry.current = 0; setState((s) => ({ ...s, live: true })); void quiet(); };
      sock.onmessage = (m) => {
        if (pongTimer) { clearTimeout(pongTimer); pongTimer = null; }
        try {
          const msg = JSON.parse(String(m.data)) as { type: string; revision?: number };
          if (msg.type === "revision" || msg.type === "heightCorrections") void quiet();
          else if (msg.type === "deleted") setState((s) => ({ ...s, deleted: true }));
        } catch { /* ignore */ }
      };
      sock.onclose = () => {
        if (ws.current === sock) ws.current = null;
        setState((s) => ({ ...s, live: false }));
        if (closed) return;
        scheduleReconnect();
      };
      sock.onerror = () => sock.close();
    }
    connect();
    const ping = setInterval(() => {
      const sock = ws.current;
      if (sock?.readyState !== WebSocket.OPEN || pongTimer) return;
      sock.send("ping");
      pongTimer = setTimeout(() => { pongTimer = null; drop(sock); }, PONG_TIMEOUT_MS);
    }, PING_MS);
    // without the WebSocket (proxy, long outage) the console still follows the server through `/changes`
    const poll = setInterval(() => { if (ws.current?.readyState !== WebSocket.OPEN) void quiet(); }, OFFLINE_POLL_MS);
    const onVisible = () => { if (document.visibilityState === "visible") void quiet(); };
    // network back: resync at once and reconnect without waiting for the backoff timer
    const onOnline = () => { void quiet(); if (!ws.current || ws.current.readyState === WebSocket.CLOSED) { if (timer) clearTimeout(timer); retry.current = 0; connect(); } };
    // network lost according to the browser: banner at once, without waiting for a request to fail
    const onOffline = () => { reportUnreachable(); setState((s) => ({ ...s, syncFailed: true })); drop(ws.current); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      closed = true; if (timer) clearTimeout(timer); if (pongTimer) clearTimeout(pongTimer); clearInterval(ping); clearInterval(poll);
      document.removeEventListener("visibilitychange", onVisible); window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline);
      const sock = ws.current;
      if (sock) { sock.onclose = null; sock.close(); }
      ws.current = null;
    };
  }, [id, sync]);

  return { ...state, mode: liveMode(state), sync, reload, setEvent };
}


function applyChanges(e: EventData, r: ChangesResponse): EventData {
  let restrictions = e.restrictions ?? [], pois = e.pois ?? [], cats = e.poiCategories ?? [];
  let header: Partial<EventData> = {};
  for (const c of r.changes) {
    switch (c.entityType) {
      case "RESTRICTION":
        restrictions = restrictions.filter((x) => x.id !== c.entityId);
        if (c.operation === "UPSERT" && c.payload) restrictions = [...restrictions, c.payload as EventData["restrictions"] extends (infer T)[] | undefined ? T : never];
        break;
      case "POI":
        pois = pois.filter((x) => x.id !== c.entityId);
        if (c.operation === "UPSERT" && c.payload) pois = [...pois, c.payload as NonNullable<EventData["pois"]>[number]];
        break;
      case "POI_CATEGORY":
        cats = cats.filter((x) => x.id !== c.entityId);
        if (c.operation === "UPSERT" && c.payload) cats = [...cats, c.payload as NonNullable<EventData["poiCategories"]>[number]];
        break;
      case "EVENT":
        if (c.operation === "UPSERT" && c.payload) {
          const h = c.payload as EventHeader;
          header = { name: h.name, description: h.description, startAt: h.startAt, endAt: h.endAt, status: h.status, settings: h.settings,
            zone: h.zone, zoneAdjusted: h.zoneAdjusted, version: h.version, updatedAt: h.updatedAt, publishedAt: h.publishedAt };
        }
        break;
      default:
        break;
    }
  }
  return {
    ...e, ...header,
    restrictions: restrictions.sort((a, b) => (a.startAt ?? "").localeCompare(b.startAt ?? "")),
    pois: pois.sort((a, b) => a.name.localeCompare(b.name, "fr")),
    poiCategories: cats.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name, "fr")),
    revision: r.revision,
    heightCorrectionsRevision: r.heightCorrectionsRevision,
    heightCorrections: r.heightCorrections ?? e.heightCorrections,
  };
}
